'use client';

import { buildAuthHref } from '@/features/auth/utils/authRedirect';
import { HubButtonLabel } from '@/features/hub/shared/HubComponents';
import readHubMessage from '@/features/hub/shared/readHubMessage';
import { clientFetch } from '@/lib/api/clientFetch';
import { isAdminUser } from '@/lib/auth/isAdminUser';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { Image, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';

const STORAGE_KEY_PREFIX = 'hub-game-suggestion-prompt:v1';
const AUTO_OPEN_STORAGE_KEY = 'hub-game-suggestion-prompt:auto-open:v1';
const SUBMITTED_REASON = 'submitted';
const DRAFT_STORAGE_KEY = 'hub-game-suggestion-draft:v1';

function persistPromptState(storageKey, reason = 'dismissed') {
  if (!storageKey || typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify({ reason, at: Date.now() }),
    );
  } catch {
    // Ignore storage failures; the prompt still works without persistence.
  }
}

export default function HubGameSuggestionPrompt({
  autoOpenOnFirstVisit = false,
  compact = false,
  currentUser = null,
  messages = {},
}) {
  const pathname = usePathname();
  const router = useRouter();
  const [viewer, setViewer] = useState(() => {
    const initialUserNumber = String(currentUser?.userNumber || '').trim();
    const initialRole = String(currentUser?.role || '').trim();

    return initialUserNumber || initialRole
      ? {
          userNumber: initialUserNumber,
          role: initialRole,
        }
      : null;
  });
  const [hasResolvedViewer, setHasResolvedViewer] = useState(() => {
    const initialUserNumber = String(currentUser?.userNumber || '').trim();
    return Boolean(initialUserNumber) || isAdminUser(currentUser);
  });
  const userNumber = String(viewer?.userNumber || '').trim();
  const canSuggest = !isAdminUser(viewer);
  const storageKey = useMemo(
    () => (userNumber ? `${STORAGE_KEY_PREFIX}:${userNumber}` : ''),
    [userNumber],
  );
  const msg = (path, fallback = '', replacements = {}) =>
    readHubMessage(messages, path, fallback, replacements);
  const [show, setShow] = useState(false);
  const [titles, setTitles] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const storedDraft = window.sessionStorage.getItem(DRAFT_STORAGE_KEY);
      if (storedDraft) {
        setTitles(storedDraft);
      }
    } catch {
      // Ignore storage failures; the form still works without draft restore.
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      if (titles.trim()) {
        window.sessionStorage.setItem(DRAFT_STORAGE_KEY, titles);
      } else {
        window.sessionStorage.removeItem(DRAFT_STORAGE_KEY);
      }
    } catch {
      // Ignore storage failures; the form still works without draft persistence.
    }
  }, [titles]);

  useEffect(() => {
    const nextUserNumber = String(currentUser?.userNumber || '').trim();
    const nextRole = String(currentUser?.role || '').trim();

    if (nextUserNumber || nextRole) {
      setViewer({ userNumber: nextUserNumber, role: nextRole });
      setHasResolvedViewer(true);
      return;
    }

    let isMounted = true;

    const loadViewer = async () => {
      try {
        const profile = await clientFetch('/api/users/profile');
        if (!isMounted) {
          return;
        }

        const fetchedUserNumber = String(profile?.userNumber || '').trim();
        const fetchedRole = String(profile?.role || '').trim();

        if (fetchedUserNumber || fetchedRole) {
          setViewer({
            userNumber: fetchedUserNumber,
            role: fetchedRole,
          });
        } else {
          setViewer(null);
        }
      } catch {
        if (!isMounted) {
          return;
        }

        setViewer(null);
      } finally {
        if (isMounted) {
          setHasResolvedViewer(true);
        }
      }
    };

    loadViewer();

    return () => {
      isMounted = false;
    };
  }, [currentUser?.role, currentUser?.userNumber]);

  useEffect(() => {
    if (
      !autoOpenOnFirstVisit ||
      !hasResolvedViewer ||
      !canSuggest ||
      typeof window === 'undefined'
    ) {
      return;
    }

    if (!storageKey) {
      try {
        if (window.localStorage.getItem(AUTO_OPEN_STORAGE_KEY)) {
          return;
        }

        window.localStorage.setItem(
          AUTO_OPEN_STORAGE_KEY,
          JSON.stringify({ at: Date.now(), path: pathname || '/hub' }),
        );
      } catch {
        // Ignore storage failures; the prompt still works without persistence.
      }

      setShow(true);
      return;
    }

    try {
      if (window.localStorage.getItem(AUTO_OPEN_STORAGE_KEY)) {
        return;
      }

      const stored = window.localStorage.getItem(storageKey);

      if (stored) {
        const parsed = JSON.parse(stored);

        if (parsed?.reason !== SUBMITTED_REASON) {
          // Older dismissed entries should not suppress future visits.
          window.localStorage.removeItem(storageKey);
        }
      }

      window.localStorage.setItem(
        AUTO_OPEN_STORAGE_KEY,
        JSON.stringify({ at: Date.now(), path: pathname || '/hub' }),
      );

      if (!stored) {
        setShow(true);
        return;
      }

      const parsed = JSON.parse(stored);

      if (parsed?.reason === SUBMITTED_REASON) {
        setShow(false);
        return;
      }

      setShow(true);
    } catch {
      setShow(true);
    }
  }, [
    autoOpenOnFirstVisit,
    canSuggest,
    hasResolvedViewer,
    pathname,
    storageKey,
  ]);

  if (!hasResolvedViewer || !canSuggest) {
    return null;
  }

  const handleOpen = () => {
    setErrorMessage('');
    setShow(true);
  };

  const handleHide = () => {
    setErrorMessage('');
    setShow(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');

    if (!titles.trim()) {
      setErrorMessage(
        msg(
          'home.gameSuggestions.errors.required',
          'Add at least one game title before sending.',
        ),
      );
      return;
    }

    if (!userNumber) {
      setShow(false);
      router.push(buildAuthHref('/login', pathname || '/hub'));
      return;
    }

    setIsSubmitting(true);

    try {
      await clientFetch('/api/hub/game-suggestions', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify({ titles }),
      });

      persistPromptState(storageKey, SUBMITTED_REASON);
      setTitles('');
      setShow(false);
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(DRAFT_STORAGE_KEY);
      }
      toast.success(
        msg(
          'home.gameSuggestions.toastSuccess',
          'Thanks - your game suggestions were sent.',
        ),
      );
    } catch (error) {
      const nextError =
        error?.data?.message ||
        error?.message ||
        msg(
          'home.gameSuggestions.toastError',
          'Unable to send your game suggestions right now.',
        );

      setErrorMessage(nextError);
      toast.error(nextError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const triggerClassName = compact
    ? 'app-button app-button--secondary hub-game-suggestion-trigger hub-game-suggestion-trigger--compact'
    : 'hub-home-profile-card__action app-button app-button--secondary hub-game-suggestion-trigger';

  return (
    <>
      <button type="button" className={triggerClassName} onClick={handleOpen}>
        <HubButtonLabel
          icon="🧩"
          label={msg('home.gameSuggestions.trigger', 'Suggest a game')}
          helper={
            compact
              ? ''
              : msg(
                  'home.gameSuggestions.triggerHelper',
                  'Tell us what should be added next',
                )
          }
        />
      </button>

      <Modal
        show={show}
        onHide={handleHide}
        centered
        className="hub-game-suggestion-modal app-glass-modal"
        backdropClassName="app-glass-modal-backdrop"
        dialogClassName="hub-game-suggestion-modal__dialog"
        contentClassName="hub-game-suggestion-modal__content"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {msg('home.gameSuggestions.title', 'Suggest games for the Hub')}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="hub-game-suggestion-modal__body">
          <p className="hub-home-panel__eyebrow hub-game-suggestion-modal__eyebrow">
            {msg('home.gameSuggestions.eyebrow', 'What should we add next?')}
          </p>
          <p className="hub-game-suggestion-modal__copy">
            {msg(
              'home.gameSuggestions.copy',
              'Share the game titles you want to see added to the Hub catalog. Add one title per line and we will review them for the next curation pass.',
            )}
          </p>

          <form
            onSubmit={handleSubmit}
            className="hub-game-suggestion-modal__form"
          >
            <div className="game-titles-control">
              <label
                className="form-group-label form-label"
                htmlFor="hub-game-suggestion-titles"
              >
                {msg('home.gameSuggestions.fieldLabel', 'Game titles')}
              </label>
              <textarea
                id="hub-game-suggestion-titles"
                name="titles"
                rows={6}
                className="form-control ps-4 hub-game-suggestion-modal__textarea"
                placeholder={msg(
                  'home.gameSuggestions.fieldPlaceholder',
                  'Marvel Rivals\nValorant\nEA Sports FC 26',
                )}
                value={titles}
                onChange={(event) => setTitles(event.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <p className="form-text form-field__helper hub-game-suggestion-modal__helper">
              {msg(
                'home.gameSuggestions.helper',
                'One title per line, up to 8 suggestions per submission.',
              )}
            </p>

            {errorMessage ? (
              <p className="hub-game-suggestion-modal__error" role="alert">
                {errorMessage}
              </p>
            ) : null}

            <div className="hub-game-suggestion-modal__actions">
              <button
                type="button"
                className="app-button app-button--ghost"
                onClick={handleHide}
                disabled={isSubmitting}
              >
                {msg('home.gameSuggestions.dismiss', 'Not now')}
              </button>
              <button
                type="submit"
                className="app-button app-button--primary gap-2"
                disabled={isSubmitting}
              >
                <Image src="/icons/send.png" alt="" width={16} height={16} />{' '}
                {isSubmitting
                  ? msg('home.gameSuggestions.submitting', 'Sending...')
                  : msg('home.gameSuggestions.submit', 'Send suggestions')}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
}
